<template>
	<div class="project-time" v-if="isReady">
		<portal-filter-bar :label="$t('global.filter')">
			<portal-search-field
				v-model="filter"
				:label="$t('global.filter')"
				:placeholder="$t('global.filter')"
				:clear-label="$t('global.clear')"
			></portal-search-field>
			<portal-select-field
				v-model="dateFilter"
				:label="$t('global.date')"
				:options="dateOptions"
				@change="loadTimerEvents"
			></portal-select-field>
		</portal-filter-bar>

		<section class="project-time-summary">
			<div class="summary-card">
				<span>{{ $t('timetrack.reports.total-time') }}</span>
				<strong>{{ $formatters.formatSeconds(metrics.totalTime) }}</strong>
			</div>
			<div class="summary-card">
				<span>{{ $t('timetrack.reports.avg-month') }}</span>
				<strong>{{ $formatters.formatSeconds(metrics.averageMonthTime) }}</strong>
			</div>
			<div class="summary-card">
				<span>{{ $t('timetrack.reports.avg-week') }}</span>
				<strong>{{ $formatters.formatSeconds(metrics.averageWeekTime) }}</strong>
			</div>
		</section>

		<timer-event-table
			class="project-time-records"
			:timer-events="projectTimerEvents"
			:filter="filter"
			hide-project
		></timer-event-table>
	</div>
	<div class="project-time-loading" v-else>
		<v-progress-circular indeterminate :size="34" :width="3" color="primary"></v-progress-circular>
	</div>
</template>

<script>
	import { DateTime } from 'luxon';
	import FilterHelpers from '@/utils/FilterHelpers';
	import TimerEventService from '@/modules/timetracking/TimerEventService';
	import TimerEventTable from '@/modules/timetracking/TimerEventTable';
	import PortalFilterBar from '@/components/filters/PortalFilterBar';
	import PortalSearchField from '@/components/filters/PortalSearchField';
	import PortalSelectField from '@/components/filters/PortalSelectField';

	export default {
		name: 'ProjectTime',

		components: {
			TimerEventTable,
			PortalFilterBar,
			PortalSearchField,
			PortalSelectField,
		},

		props: {
			projectId: { type: [String, Number], required: true },
		},

		data() {
			return {
				timerEventService: new TimerEventService(),
				timerEvents: [],
				filter: '',
				dateFilter: 'past-60-days',
				earliest: null,
				latest: null,
				isReady: false,
				dateOptions: FilterHelpers.getDateOptions([
					'this-month',
					'last-month',
					'last-last-month',
					'past-60-days',
					'past-90-days',
					'past-6-months',
					'past-12-months',
					'this-year',
					'last-year',
				]),
			};
		},

		mounted() {
			this.loadTimerEvents();
		},

		computed: {
			projectTimerEvents() {
				return this.timerEvents.filter(
					(event) => String(event.projectId) === String(this.projectId)
				);
			},

			metrics() {
				const totalTime = this.projectTimerEvents.reduce(
					(total, event) => total + (event.durationSeconds || 0),
					0
				);
				return {
					totalTime,
					averageMonthTime: this.averageForPeriod('month'),
					averageWeekTime: this.averageForPeriod('week'),
				};
			},
		},

		methods: {
			loadTimerEvents() {
				const range = FilterHelpers.getEarliestAndLatest(this.dateFilter, false);
				this.earliest = range.earliest;
				this.latest = range.latest;
				this.isReady = false;
				this.timerEventService
					.getTimerEvents(this.earliest, this.latest)
					.then((res) => {
						this.timerEvents.splice(
							0,
							this.timerEvents.length,
							...res.data.map((event) => {
								const startDate = DateTime.fromISO(event.timerStart);
								return {
									...event,
									startDate,
									endDate: startDate.plus({ seconds: event.duration }),
									durationSeconds: event.duration,
								};
							})
						);
					})
					.finally(() => {
						this.isReady = true;
					});
			},

			averageForPeriod(period) {
				if (!this.projectTimerEvents.length) return 0;
				const totals = new Map();
				this.projectTimerEvents.forEach((event) => {
					const key = event.startDate.startOf(period).toISODate();
					totals.set(key, (totals.get(key) || 0) + event.durationSeconds);
				});
				const sum = [...totals.values()].reduce((total, value) => total + value, 0);
				return totals.size ? sum / totals.size : 0;
			},
		},
	};
</script>

<style scoped lang="scss">
	.project-time,
	.project-time-loading {
		min-height: 320px;
	}

	.project-time {
		display: grid;
		gap: 16px;
	}

	.project-time-loading {
		display: grid;
		place-items: center;
	}

	.project-time-summary {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
	}

	.summary-card {
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
	}

	.summary-card {
		display: grid;
		gap: 7px;
		padding: 17px;
	}

	.summary-card span {
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 650;
	}

	.summary-card strong {
		color: var(--portal-text);
		font-size: clamp(20px, 2.4vw, 28px);
		font-weight: 680;
		letter-spacing: -0.025em;
	}

	.project-time-records.timer-event-table-wrap {
		padding: 0;
	}

	@media (max-width: 760px) {
		.project-time-summary {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>
